import type {
  CanvasDataSource,
  SchemaTableWithColumns,
  SchemaRelationshipResponse,
  ViewDefinition,
  SchemaColumnResponse,
} from "../../../types";
import type { HierarchyConfig } from "../elements/hierarchyGraph";
import { generateCanvasDataSourceAlias } from "../../../views/canvasDataSourceAlias";
export interface HierarchyBuilderField {
  name: string;
  label: string;
  column: SchemaColumnResponse;
  focus: boolean;
}

/** Same projected names as ViewQueryService._output_aliases; focus and self-joined columns remain distinct. */
export function hierarchyViewFields(
  view: ViewDefinition,
  tables: SchemaTableWithColumns[],
  relationships: SchemaRelationshipResponse[],
): HierarchyBuilderField[] {
  const focusId = String(view.focus_table_id);
  const columns = new Map(
    tables
      .flatMap((table) => table.columns)
      .map((column) => [column.id, column]),
  );
  const joined = new Map<string, { table: string; alias: string }>();
  for (const join of [...(view.joins || [])].sort(
    (a, b) => a.join_order - b.join_order,
  )) {
    const relation = relationships.find(
      (item) => item.id === join.relationship_id,
    );
    if (!relation) continue;
    const source =
      (join.parent_join_id ? joined.get(join.parent_join_id) : undefined) ||
      [{ table: focusId, alias: "t0" }, ...joined.values()].find((candidate) =>
        [relation.from_table_id, relation.to_table_id].includes(
          candidate.table,
        ),
      );
    if (source)
      joined.set(join.id, {
        table:
          source.table === relation.from_table_id
            ? relation.to_table_id
            : relation.from_table_id,
        alias: join.table_alias || `j${join.join_order}`,
      });
  }
  const visible = (view.fields || []).filter(
    (field) =>
      (field.is_visible ?? field.visible ?? true) &&
      (columns.has(field.column_id) || field.column),
  );
  const counts = new Map<string, number>();
  for (const field of visible) {
    const column = columns.get(field.column_id) || field.column!;
    counts.set(column.column_name, (counts.get(column.column_name) || 0) + 1);
  }
  const used = new Set<string>();
  return visible.flatMap((field) => {
    const column = columns.get(field.column_id) || field.column!;
    const candidates = [...joined.values()].filter(
      (item) => item.table === column.table_id,
    );
    const alias =
      field.source_table_alias ||
      (column.table_id === focusId
        ? "t0"
        : candidates.length === 1
          ? candidates[0].alias
          : "");
    if (!alias) return [];
    let name =
      counts.get(column.column_name) === 1 && !used.has(column.column_name)
        ? column.column_name
        : `${alias === "t0" ? "focus" : alias}__${column.column_name}`;
    const base = name;
    let suffix = 2;
    while (used.has(name)) name = `${base}_${suffix++}`;
    used.add(name);
    return [
      {
        name,
        label:
          field.display_label ||
          field.display_name ||
          column.display_name ||
          name,
        column,
        focus: alias === "t0" && column.table_id === focusId,
      },
    ];
  });
}
const uuid = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
const nonblank = (value: unknown): value is string =>
  typeof value === "string" && !!value.trim();
/** Required mapping checks happen before any builder save; server also verifies actual relation/column metadata. */
export function normalizeHierarchyConfig(
  raw: unknown,
  sources: CanvasDataSource[],
): HierarchyConfig {
  const fail = (): never => {
    throw new Error(
      "Hierarchy requires configured item Views, key/label fields and existing relationships",
    );
  };
  if (!raw || typeof raw !== "object") return fail();
  const config = raw as HierarchyConfig;
  if (
    !Array.isArray(config.nodes) ||
    !config.nodes.length ||
    !Array.isArray(config.relationships) ||
    !config.relationships.length
  )
    return fail();
  const resolve = (alias: string) => {
    const index = sources.findIndex((source, i) =>
      [
        source.id,
        source.name,
        generateCanvasDataSourceAlias(source.name, i),
      ].includes(alias),
    );
    if (index < 0 || !sources[index].view_id) return fail();
    return generateCanvasDataSourceAlias(sources[index].name, index);
  };
  const nodes = config.nodes.map((node) => {
    if (
      !nonblank(node.table_id) ||
      !uuid.test(node.table_id) ||
      !nonblank(node.id_field) ||
      !nonblank(node.label_field)
    )
      return fail();
    return { ...node, data_source_alias: resolve(node.data_source_alias) };
  });
  const aliases = new Set(nodes.map((node) => node.data_source_alias));
  if (aliases.size !== nodes.length) return fail();
  const relationships = config.relationships.map((relation) => {
    const from = resolve(relation.from_data_source_alias),
      to = resolve(relation.to_data_source_alias);
    if (
      !aliases.has(from) ||
      !aliases.has(to) ||
      !nonblank(relation.from_field) ||
      !nonblank(relation.to_field) ||
      !Array.isArray(relation.relationship_ids) ||
      relation.relationship_ids.some((id) => !uuid.test(id))
    )
      return fail();
    if (
      relation.link_data_source_alias
        ? relation.relationship_ids.length !== 2 ||
          new Set(relation.relationship_ids).size !== 2
        : relation.relationship_ids.length !== 1
    )
      return fail();
    return {
      ...relation,
      from_data_source_alias: from,
      to_data_source_alias: to,
      ...(relation.link_data_source_alias
        ? { link_data_source_alias: resolve(relation.link_data_source_alias) }
        : {}),
    };
  });
  const initial = config.initial_depth ?? 2,
    max = config.max_depth ?? 50;
  if (
    !["tree", "list", "flow", "radial"].includes(config.layout || "tree") ||
    !Number.isInteger(initial) ||
    !Number.isInteger(max) ||
    initial < 1 ||
    max > 50 ||
    initial > max
  )
    return fail();
  const starting = Object.fromEntries(
    Object.entries(config.starting_items || {}).map(([alias, ids]) => {
      const normalized = resolve(alias);
      if (
        !aliases.has(normalized) ||
        !Array.isArray(ids) ||
        ids.length > 100 ||
        ids.some(
          (id) =>
            !(
              nonblank(id) ||
              (typeof id === "number" && Number.isSafeInteger(id))
            ),
        )
      )
        return fail();
      return [normalized, ids];
    }),
  );
  return {
    ...config,
    nodes,
    relationships,
    layout: config.layout || "tree",
    initial_depth: initial,
    max_depth: max,
    starting_items: starting,
  };
}
